import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { escapeHtml, SafeHTML } from '@/lib/safeHtml';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, ArrowLeft, Camera, Sparkles, X, FileText, PartyPopper, Zap, Trophy, Image as ImageIcon, Film, History } from 'lucide-react';
import { toast } from 'sonner';
import AhaSummaryCard from '@/components/AhaSummaryCard';
import GuideChips from '@/components/chat/GuideChips';
import ChatHistoryDrawer from '@/components/chat/ChatHistoryDrawer';
import { Hint } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { useScreenTip } from '@/hooks/useScreenTip';

// Iter 59 (Feb 2026) — Mid-chat Graphics nudge. The buddy will
// occasionally weave in a natural "hop over to the Graphics tab" hint
// with a [[GRAPHICS_HINT]] marker; the backend strips the marker and
// flips `graphics_hint: true` on the message. We render a warm quick-
// jump chip that opens the Graphics tab for THIS chapter in a NEW tab
// so the chat stays alive and the student can come right back.
function GraphicsPeekChip({ subject, topic, navigate, onView }) {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (topic) params.set('topic', topic);
  const href = `/gallery${params.toString() ? '?' + params.toString() : ''}`;
  return (
    <div className="mt-3" data-testid="graphics-peek">
      <button
        onClick={() => {
          // Iter 60: mark that the student has viewed the graphics so
          // the buddy asks a warm follow-up ("did it click?") on the
          // very next message they send. Reset in sendMessage after use.
          try { onView && onView(); } catch { /* no-op */ }
          window.open(href, '_blank', 'noopener');
        }}
        data-testid="graphics-peek-btn"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-[12px] font-heading bg-gradient-to-r from-fuchsia-500 via-pink-500 to-orange-400 hover:scale-105 active:scale-95 transition-transform shadow-md shadow-pink-500/25 animate-pulse-slow"
      >
        <ImageIcon className="w-3.5 h-3.5" />
        Peek at the visuals
        <Film className="w-3.5 h-3.5 opacity-80" />
      </button>
      <p className="text-[10px] text-slate-400 font-body mt-1 ml-1">Opens in a new tab — chat stays right here.</p>
    </div>
  );
}

// Iter 58 (Feb 2026) — Closure Funnel. Appears under the assistant bubble
// whenever the buddy declares closure (student said "I got it" or the AI
// emitted [[CELEBRATE]]). Points the student toward Flashcards → Puzzle →
// Quiz → Graphics so the concept reaches a real "closed loop" and the
// student walks away with something to practice on.
function ClosureFunnel({ subject, topic, navigate }) {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (topic) params.set('topic', topic);
  const q = params.toString() ? `?${params.toString()}` : '';
  const chips = [
    { label: 'Flashcards',    icon: Zap,       to: `/flashcards${q}`, tone: 'from-emerald-500 to-teal-500', testid: 'closure-flashcards' },
    { label: 'Puzzle',        icon: Sparkles,  to: `/puzzle${q}`,     tone: 'from-fuchsia-500 to-pink-500', testid: 'closure-puzzle' },
    { label: 'Quiz',          icon: Trophy,    to: `/quiz${q}`,       tone: 'from-amber-500 to-orange-500', testid: 'closure-quiz' },
    { label: 'Infographics',  icon: ImageIcon, to: `/gallery${q}`,    tone: 'from-blue-500 to-indigo-500',  testid: 'closure-infographic' },
    { label: 'Cinegraphics',  icon: Film,      to: `/gallery${q}&tab=cinegraphics`, tone: 'from-violet-500 to-purple-500', testid: 'closure-cinegraphic' },
  ];
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="closure-funnel">
      <p className="text-[11px] font-heading text-slate-500 mb-2 flex items-center gap-1.5">
        <PartyPopper className="w-3 h-3 text-amber-500" /> Ready to lock this in?
      </p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map(c => (
          <button key={c.label}
            data-testid={c.testid}
            onClick={() => navigate(c.to)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-[11px] font-heading bg-gradient-to-r ${c.tone} hover:scale-105 active:scale-95 transition-transform shadow-sm`}>
            <c.icon className="w-3 h-3" />
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Renders AI chat content with proper formatting for math, code blocks, bold, lists
function FormattedMessage({ content }) {
  if (!content) return null;
  const parts = content.split(/(```[\s\S]*?```)/g);
  return parts.map((part, i) => {
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).replace(/^\w*\n/, '');
      return <pre key={i} className="bg-slate-100 rounded-xl p-3 my-2 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words border border-slate-200">{code}</pre>;
    }
    return <FormattedText key={i} text={part} />;
  });
}

function cleanLatex(text) {
  return text
    .replace(/\\\[/g, '\n').replace(/\\\]/g, '\n')
    .replace(/\\\(/g, '').replace(/\\\)/g, '')
    .replace(/\$\$([^$]+)\$\$/g, '\n$1\n')
    .replace(/\$([^$]+)\$/g, '$1')
    .replace(/\\Rightarrow/gi, ' \u2192 ')
    .replace(/\\rightarrow/gi, ' \u2192 ')
    .replace(/\\Leftarrow/gi, ' \u2190 ')
    .replace(/\\leftarrow/gi, ' \u2190 ')
    .replace(/\\Leftrightarrow/gi, ' \u21D4 ')
    .replace(/\\times/gi, ' \u00D7 ')
    .replace(/\\cdot/gi, ' \u00B7 ')
    .replace(/\\div/gi, ' \u00F7 ')
    .replace(/\\pm/gi, ' \u00B1 ')
    .replace(/\\neq/gi, ' \u2260 ')
    .replace(/\\leq/gi, ' \u2264 ')
    .replace(/\\geq/gi, ' \u2265 ')
    .replace(/\\approx/gi, ' \u2248 ')
    .replace(/\\infty/gi, '\u221E')
    .replace(/\\pi/gi, '\u03C0')
    .replace(/\\theta/gi, '\u03B8')
    .replace(/\\alpha/gi, '\u03B1')
    .replace(/\\beta/gi, '\u03B2')
    .replace(/\\gamma/gi, '\u03B3')
    .replace(/\\delta/gi, '\u03B4')
    .replace(/\\lambda/gi, '\u03BB')
    .replace(/\\sigma/gi, '\u03C3')
    .replace(/\\mu/gi, '\u03BC')
    .replace(/\\sqrt\{([^}]+)\}/g, '\u221A($1)')
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
    .replace(/\^{([^}]+)}/g, '\u00B2'.length ? '^$1' : '^$1')
    .replace(/_{([^}]+)}/g, '_$1')
    .replace(/\\text\{([^}]+)\}/g, '$1')
    .replace(/\\mathrm\{([^}]+)\}/g, '$1')
    .replace(/\\quad/g, '  ')
    .replace(/\\;/g, ' ')
    .replace(/\\,/g, ' ')
    .replace(/\\!/g, '')
    .replace(/\\\\/g, '\n')
    .replace(/\\[{}]/g, '');
}

function FormattedText({ text }) {
  const cleaned = cleanLatex(text);
  const lines = cleaned.split('\n');
  return lines.map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    // SEC-003: escape raw text FIRST so any user/LLM `<script>` becomes
    // literal `&lt;script&gt;`. Only then apply the safe format regexes
    // (which inject `<strong>` / `<code>` — allow-listed in safeHtml.js).
    const safe = escapeHtml(line);
    const formatted = safe
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono text-blue-700">$1</code>')
      .replace(/###\s*(.*)/g, '<strong class="text-base">$1</strong>');
    return (
      <span key={i}>
        <SafeHTML html={formatted} />
        {i < lines.length - 1 && <br />}
      </span>
    );
  });
}

export default function ChatPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [eli5, setEli5] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(!sessionId);
  useScreenTip('chat', HELP.tips.chat);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  // Iter 58: document (.txt/.pdf) upload via the Snap & Solve panel
  const [documentBase64, setDocumentBase64] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  // Iter 58: celebration overlay when the buddy declares closure
  const [celebrateMessageId, setCelebrateMessageId] = useState(null);
  // Iter 60: mark that the student tapped the "Peek at visuals" chip so
  // the next outgoing message tells the backend they returned from
  // viewing graphics — the buddy then opens with a warm "did it click?"
  // follow-up. Reset once the payload is sent.
  const [pendingGraphicsViewed, setPendingGraphicsViewed] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    apiGet('/chat/sessions').then(d => setSessions(d.sessions || [])).catch(() => {});
    if (user?.class_level) {
      apiGet(`/onboarding/subjects?class_level=${user.class_level}`).then(d => setSubjects(d.subjects || [])).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (sessionId) {
      setShowNewChat(false);
      apiGet(`/chat/sessions/${sessionId}/messages`).then(d => {
        setActiveSession(d.session);
        setMessages(d.messages || []);
      }).catch(() => {});
    }
  }, [sessionId]);

  useEffect(() => {
    if (selectedSubject && user?.class_level) {
      apiGet(`/onboarding/topics?subject=${encodeURIComponent(selectedSubject)}&class_level=${user.class_level}`).then(d => setTopics(d.topics || [])).catch(() => {});
    }
  }, [selectedSubject, user]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const startNewChat = async () => {
    if (!selectedSubject || !selectedTopic) { toast.error('Please select subject and topic'); return; }
    try {
      const session = await apiPost('/chat/sessions', { subject: selectedSubject, topic: selectedTopic });
      setActiveSession(session);
      setMessages([{ message_id: 'greeting', role: 'assistant', content: session.greeting, created_at: new Date().toISOString() }]);
      setShowNewChat(false);
      navigate(`/chat/${session.session_id}`, { replace: true });
    } catch (e) { toast.error('Failed to start chat'); }
  };

  const sendMessage = async () => {
    if (!input.trim() && !imageBase64 && !documentBase64) return;
    if (!activeSession) return;
    setSending(true);
    const userContent = input.trim();
    setInput('');
    const previewLabel = documentName ? `📎 ${documentName}` : (imageBase64 ? '(Image uploaded)' : userContent);
    setMessages(prev => [...prev, { message_id: `temp_${Date.now()}`, role: 'user', content: userContent || previewLabel, created_at: new Date().toISOString() }]);
    const imgB64 = imageBase64;
    const docB64 = documentBase64;
    const docName = documentName;
    setImageBase64(null);
    setImagePreview(null);
    setDocumentBase64(null);
    setDocumentName(null);

    try {
      const res = await apiPost(`/chat/sessions/${activeSession.session_id}/messages`, {
        content: userContent, eli5_mode: eli5,
        image_base64: imgB64 || undefined,
        document_base64: docB64 || undefined,
        document_filename: docName || undefined,
        graphics_viewed: pendingGraphicsViewed || undefined,
      });
      // Iter 60: clear the "just viewed graphics" flag after it's been
      // reported to the backend — the follow-up should fire exactly once.
      if (pendingGraphicsViewed) setPendingGraphicsViewed(false);
      setMessages(prev => {
        const filtered = prev.filter(m => !m.message_id.startsWith('temp_'));
        return [...filtered, res.user_message, res.ai_message];
      });
      // Iter 58: buddy declared closure — trigger celebration + funnel chips
      if (res.ai_message?.is_closure) {
        setCelebrateMessageId(res.ai_message.message_id);
        // Auto-dismiss the confetti overlay after 3s; the funnel chips stay.
        setTimeout(() => setCelebrateMessageId(null), 3000);
      }
    } catch (e) {
      const errMsg = e.message || '';
      if (errMsg.includes('limit')) toast.error('Daily limit reached! Come back tomorrow.');
      else toast.error('Failed to send message');
    }
    setSending(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    const isDoc = name.endsWith('.pdf') || name.endsWith('.txt');
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      if (isDoc) {
        setDocumentBase64(base64);
        setDocumentName(file.name);
        setImageBase64(null);
        setImagePreview(null);
      } else {
        setImageBase64(base64);
        setImagePreview(reader.result);
        setDocumentBase64(null);
        setDocumentName(null);
      }
    };
    reader.readAsDataURL(file);
    // Clear so re-selecting the same file re-triggers change
    e.target.value = '';
  };
  const handleImageUpload = handleFileUpload;  // kept for legacy call sites

  const getSummary = async () => {
    if (!activeSession) return;
    try {
      const s = await apiPost(`/chat/sessions/${activeSession.session_id}/summary`, {});
      setSummary(s);
      setShowSummary(true);
    } catch { toast.error('Could not generate summary'); }
  };

  // New chat topic selector
  if (showNewChat) {
    return (
      <div className="max-w-lg mx-auto px-4 pt-6" data-testid="new-chat-page">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="chat-back-btn" onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-heading text-xl font-bold text-slate-900">New Chat Session</h1>
        </div>

        {/* Previous sessions */}
        {sessions.length > 0 && (
          <div className="mb-6">
            <h3 className="font-heading text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Recent Sessions</h3>
            <div className="space-y-2">
              {sessions.slice(0, 5).map(s => (
                <button key={s.session_id} data-testid={`session-${s.session_id}`}
                  onClick={() => navigate(`/chat/${s.session_id}`)}
                  className="w-full p-4 rounded-2xl bg-white border border-slate-100 text-left hover:border-blue-200 transition-all">
                  <p className="font-heading text-sm font-semibold text-slate-800">{s.label || s.topic || s.subject || 'Ask me anything'}</p>
                  <p className="text-xs text-slate-500 font-body">{s.subject || 'Open chat'} &middot; {s.message_count} messages</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subject & Topic Selection */}
        <div className="glass-panel rounded-3xl p-6 space-y-4">
          <h3 className="font-heading text-base font-semibold text-slate-800">Start a new session</h3>
          <select data-testid="chat-subject-select" value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setSelectedTopic(''); }}
            className="w-full p-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
            <option value="">Select Subject</option>
            {subjects.map(s => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
          </select>
          {selectedSubject && (
            <select data-testid="chat-topic-select" value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)}
              className="w-full p-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
              <option value="">Select Topic</option>
              {topics.map(t => <option key={t.topic_id} value={t.name}>{t.name}</option>)}
            </select>
          )}
          <Button data-testid="start-chat-btn" onClick={startNewChat} disabled={!selectedSubject || !selectedTopic}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading">
            Start Chat
          </Button>
        </div>
      </div>
    );
  }

  // Active chat
  return (
    <div className="absolute inset-0 flex flex-col max-w-lg mx-auto" data-testid="chat-page">
      {/* Header */}
      <div className="glass-panel-strong px-4 py-3 flex items-center justify-between z-10 rounded-b-2xl">
        <div className="flex items-center gap-3">
          <button data-testid="chat-back" onClick={() => { setShowNewChat(true); setActiveSession(null); navigate('/chat'); }}
            className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div>
            <p className="font-heading text-sm font-semibold text-slate-800">{activeSession?.topic || 'Chat'}</p>
            <p className="text-xs text-slate-500 font-body">{activeSession?.subject}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="chat-history-btn" onClick={() => setHistoryOpen(true)}
            className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors" title="Your chats">
            <History className="w-4 h-4 text-slate-500" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 font-body leading-tight max-w-[70px] text-right">Explain It Like I'm 5</span>
            <Hint text={HELP.study.eli5} side="bottom" align="end">
              <Switch data-testid="eli5-toggle" checked={eli5} onCheckedChange={setEli5} className="data-[state=checked]:bg-blue-500" />
            </Hint>
          </div>
          <Hint text={HELP.study.summary} side="bottom" align="end">
            <button data-testid="chat-summary-btn" onClick={getSummary} className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-amber-500" />
            </button>
          </Hint>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-4 custom-scrollbar">
        <div className="space-y-4">
          {messages.map((msg, i) => (
            <div key={msg.message_id || i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
              <div className={`max-w-[92%] sm:max-w-[85%] px-4 py-3 ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}`}>
                <div className="text-sm font-body text-slate-700 leading-relaxed chat-content">
                  <FormattedMessage content={msg.content} />
                </div>
                {/* Iter 59: mid-chat Graphics nudge — appears whenever the
                    buddy naturally weaves in a "peek at the visuals"
                    suggestion (marked with [[GRAPHICS_HINT]] on the AI
                    side). One quick pill instead of a full funnel — the
                    student jumps straight to the Graphics tab for THIS
                    chapter, then comes right back to keep chatting. */}
                {msg.role === 'assistant' && msg.graphics_hint && (
                  <GraphicsPeekChip
                    subject={activeSession?.subject}
                    topic={activeSession?.topic}
                    navigate={navigate}
                    onView={() => setPendingGraphicsViewed(true)}
                  />
                )}
                {/* Iter 58: closure funnel — appears under any assistant
                    message the buddy tagged as closure. Points the student
                    to Flashcards → Puzzle → Quiz → Graphics so the concept
                    reaches a real "closed loop". */}
                {msg.role === 'assistant' && msg.guide?.length > 0 && (
                  <GuideChips guide={msg.guide}
                    subject={activeSession?.subject} topic={activeSession?.topic} />
                )}
                {msg.role === 'assistant' && msg.is_closure && (
                  <ClosureFunnel
                    subject={activeSession?.subject}
                    topic={activeSession?.topic}
                    navigate={navigate}
                  />
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start animate-slide-up">
              <div className="chat-bubble-ai px-4 py-3">
                <div className="flex gap-1.5"><div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" /><div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'0.1s'}} /><div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay:'0.2s'}} /></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Iter 58: closure celebration overlay — big Trophy + confetti-ish
          gradient bursts on the corners. Shows for 3s then disappears
          (the funnel chips inside the message bubble persist). */}
      {celebrateMessageId && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center" data-testid="closure-celebration">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-200/30 via-rose-200/20 to-transparent animate-fade-in" />
          <div className="relative flex flex-col items-center gap-3 animate-bounce">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/50">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <p className="font-heading text-lg text-slate-800 bg-white/90 px-4 py-2 rounded-full shadow-lg">
              Concept unlocked! ✨
            </p>
          </div>
        </div>
      )}

      {/* Uploaded preview — image OR document chip */}
      {imagePreview && (
        <div className="px-4 pb-2">
          <div className="relative inline-block">
            <img src={imagePreview} alt="Upload" className="h-20 rounded-xl border border-slate-200" />
            <button data-testid="remove-image" onClick={() => { setImagePreview(null); setImageBase64(null); }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
      {documentName && !imagePreview && (
        <div className="px-4 pb-2">
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-100" data-testid="doc-preview">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-body text-slate-700 max-w-[220px] truncate">{documentName}</span>
            <button data-testid="remove-document" onClick={() => { setDocumentBase64(null); setDocumentName(null); }}
              className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-4 pb-4 pt-2">
        <div className="flex items-center gap-2 glass-panel rounded-2xl px-3 py-2">
          {/* Iter 58: Snap & Solve now accepts images + .pdf + .txt (like
              Emergent's own uploader). Backend extracts the text server-side
              for docs and passes it to the LLM as an authoritative reference. */}
          <input type="file" ref={fileInputRef} accept="image/*,.pdf,.txt,application/pdf,text/plain" className="hidden" onChange={handleFileUpload} />
          <Hint text={HELP.study.snapSolve} side="top" align="start">
            <button data-testid="snap-solve-btn" onClick={() => fileInputRef.current?.click()}
              className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-400 flex items-center justify-center text-white flex-shrink-0 hover:scale-105 transition-transform">
              <Camera className="w-4 h-4" />
            </button>
          </Hint>
          <input data-testid="chat-input" type="text" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask your doubt..." disabled={sending}
            className="flex-1 bg-transparent text-sm font-body text-slate-700 placeholder-slate-400 outline-none" />
          <button data-testid="chat-send-btn" onClick={sendMessage} disabled={sending || (!input.trim() && !imageBase64 && !documentBase64)}
            className="w-9 h-9 rounded-xl bg-blue-500 hover:bg-blue-600 flex items-center justify-center text-white flex-shrink-0 disabled:opacity-40 transition-all">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showSummary && summary && <AhaSummaryCard summary={summary} onClose={() => setShowSummary(false)} />}
      <ChatHistoryDrawer open={historyOpen} onOpenChange={setHistoryOpen}
        activeSessionId={activeSession?.session_id}
        onOpenSession={(id) => navigate(`/chat/${id}`)} />
    </div>
  );
}
